import { tap } from 'rxjs/operators'
import * as moment from 'moment'
import * as map from 'lodash/map'
import { Observable, Subject, empty } from 'rxjs'
import { GetFunction, PostFunction, DeleteFunction, PatchFunction } from 'interfaces/ajax'
import * as flatten from 'lodash/flatten'
import { AdditionalInfoHeader, GetQueryParams } from 'interfaces/ajax/base'
import { prepareUrl } from 'tangle-shared'
import { deviceAppStateSelector } from 'selectors/deviceSelector'
import { getStore } from 'store'
import { ajax, AjaxResponse } from 'rxjs/ajax'
import { catchAjaxSafe } from 'rxOperators/catchSafe'
import { captureEndpointError } from 'helpers/errorHelpers'

const CONTENT_TYPE_HEADER = 'Content-Type'
const CLIENT_TIME_HEADER = 'X-Client-Time'
export const DEVICE_HASH_HEADER = 'X-Device-Hash' as const
const APP_VERSION_HEADER = 'X-App-Version'
const WEB_VERSION_HEADER = 'X-Web-Version'

export const getDefaultHeaders = () => {
  const { appVersion, deviceId } = deviceAppStateSelector(getStore().getState())

  return {
    [CLIENT_TIME_HEADER]: moment().format(),
    ...deviceId ? { [DEVICE_HASH_HEADER]: deviceId } : {},
    ...appVersion ? { [APP_VERSION_HEADER]: appVersion } : {},
    ...typeof __VERSION__ !== 'undefined' ? { [WEB_VERSION_HEADER]: __VERSION__ } : {},
  }
}

export const urlSearchParamsToGetQueryParams = (searchParams: URLSearchParams): GetQueryParams => {
  const queryParams: GetQueryParams = {}

  searchParams.forEach((value, key) => {
    if (!(key in queryParams)) {
      queryParams[key] = value
      return
    }


    const existingValue = queryParams[key]
    if (Array.isArray(existingValue)) {
      (existingValue as any[]).push(value)
      return
    }

    if (existingValue) {
      queryParams[key] = [String(existingValue), value]
    }
  })

  return queryParams
}

// TODO refactor and merge with encodeUrlParams
const prepareGetParams = (params: GetQueryParams) => {
  const mappedParams = map(params, (value: GetQueryParams[keyof GetQueryParams], key: keyof GetQueryParams) => {
    if (value === undefined || value === null) {
      return undefined
    }

    if (Array.isArray(value)) {
      return (value as (string | number)[])
        .map((v) => `${ encodeURIComponent(String(key)) }=${ encodeURIComponent(String(v)) }`)
        .join('&')
    }

    return `${ encodeURIComponent(String(key)) }=${ encodeURIComponent(String(value)) }`
  })

  // remove `undefined`s from params
  const parts: string[] = []
  flatten(mappedParams).forEach((part) => {
    if (typeof part !== 'string' || part === '') {
      return
    }
    parts.push(part)
  })
  return parts.join('&')
}

type UrlWithParams = {
  url: string
  params?: {
    [key: string]: string | number
  }
}

let API_URL: string | null = null
let WITH_CREDENTIALS = false
export const initializeAjax = (apiUrl: string) => {
  API_URL = apiUrl
  WITH_CREDENTIALS = API_URL !== ''
}

const prepareUrlString = (url: string | UrlWithParams, queryParams?: GetQueryParams) => {
  let stringUrl = API_URL + (typeof url === 'string' ? url : prepareUrl(url.url, url.params))
  if (queryParams) {
    const query = prepareGetParams(queryParams)
    if (query !== '') {
      stringUrl += '?' + query
    }
  }
  return stringUrl
}

const flushDateSubject: Subject<AdditionalInfoHeader> = new Subject()
export const flushDate$ = flushDateSubject.asObservable()

const ajaxLetFn = (subject: Observable<AjaxResponse>) =>
  subject.pipe(
    tap((response) => {
      const additionalInfoHeader = response.xhr.getResponseHeader('X-Additional-Info')
      if (additionalInfoHeader) {
        let additionalInfo: AdditionalInfoHeader | null = null
        try {
          additionalInfo = JSON.parse(atob(additionalInfoHeader.split('.')[1]))
        } catch (O_o) {}

        if (additionalInfo) {
          flushDateSubject.next(additionalInfo)
        }
      }
    })
  )

const getLocal = (url: string | UrlWithParams, queryParams?: GetQueryParams, headers?: Object): Observable<AjaxResponse> => {
  return ajax({
    url: prepareUrlString(url, queryParams),
    method: 'GET',
    headers: {
      ...getDefaultHeaders(),
      ...headers || {},
    },
    withCredentials: WITH_CREDENTIALS,
  })
    .pipe(ajaxLetFn)
}

export const get = getLocal as GetFunction

type BodyRequestParams = {
  query?: GetQueryParams
  body?: any
}

export const deleteAjax: DeleteFunction = (url: string | UrlWithParams, params?: BodyRequestParams): Observable<AjaxResponse> => {
  return ajax({
    url: prepareUrlString(url, params && params.query),
    method: 'DELETE',
    headers: {
      ...getDefaultHeaders(),
      [CONTENT_TYPE_HEADER]: 'application/json',
    },
    body: params && params.body ? params.body : {},
    withCredentials: WITH_CREDENTIALS,
  })
    .pipe(ajaxLetFn)
}

const postGeneric = (url: string | UrlWithParams, params?: BodyRequestParams, headers: Object = {}): Observable<AjaxResponse> => {
  return ajax({
    url: prepareUrlString(url, params && params.query),
    method: 'POST',
    headers: {
      ...getDefaultHeaders(),
      ...headers,
    },
    body: params && params.body ? params.body : {},
    withCredentials: WITH_CREDENTIALS,
  })
    .pipe(ajaxLetFn)
}

const getPlainPostHeaders = () => ({
  [CONTENT_TYPE_HEADER]: 'application/json',
})

export const post: PostFunction = (url: string | UrlWithParams, params?: BodyRequestParams, headers?: Object): Observable<AjaxResponse> => {
  return postGeneric(url, params, {
    ...getPlainPostHeaders(),
    ...headers || {},
  })
}

export const postMultipart: PostFunction = (url: string | UrlWithParams, params?: BodyRequestParams, headers?: Object): Observable<AjaxResponse> => {
  return postGeneric(url, params, headers)
}

export const patch: PatchFunction = (url: string | UrlWithParams, params?: BodyRequestParams, headersParam?: Object): Observable<AjaxResponse> => {
  const headers = {
    ...getDefaultHeaders(),
    [CONTENT_TYPE_HEADER]: 'application/json',
    ...headersParam || {},
  }
  return ajax({
    url: prepareUrlString(url, params && params.query),
    method: 'PATCH',
    headers,
    body: params && params.body ? params.body : {},
    withCredentials: WITH_CREDENTIALS,
  })
    .pipe(ajaxLetFn)
}

export const IS_SEND_BEACON_SUPPORTED = 'sendBeacon' in navigator

export const sendBeacon: PostFunction = (url: string | UrlWithParams, params?: BodyRequestParams, headersParam?: Object): Observable<AjaxResponse> => {
  if (!IS_SEND_BEACON_SUPPORTED) {
    return postGeneric(url, params, headersParam).pipe(
      catchAjaxSafe((error) => {
        captureEndpointError(error)
        return empty()
      }) as any
    )
  }

  const urlString = prepareUrlString(url, params && params.query)
  // `sendBeacon` doesn't allow custom headers so in order to send `X-Device-hash` header we have to send it over post body
  // see https://usefulangle.com/post/63/javascript-navigator-sendbeacon-set-form-http-header
  navigator.sendBeacon(urlString, JSON.stringify({
    ...params && params.body ? params.body : {},
    headers: {
      ...getDefaultHeaders(),
      ...headersParam || {},
    },
  }))

  return empty()
}

